'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { FileText, ArrowLeft, Download, Printer, Search, ChevronRight, BarChart3, ClipboardList, ShoppingCart, Package, Truck, Receipt, RotateCcw, TrendingUp, DollarSign,  } from 'lucide-react';
import { formatUGX } from '../../components/procurementData';
import Icon from '@/components/ui/AppIcon';


interface ReportDef {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  category: string;
  color: string;
}

const REPORTS: ReportDef[] = [
  { id: 'pr-report', title: 'Purchase Requisition Report', description: 'All requisitions with status, department, requester, and amounts.', icon: ClipboardList, category: 'Requisitions', color: 'bg-blue-500' },
  { id: 'approval-report', title: 'Approval Report', description: 'Approval actions, approvers, timelines, and comments.', icon: ClipboardList, category: 'Requisitions', color: 'bg-indigo-500' },
  { id: 'po-report', title: 'Purchase Order Report', description: 'All POs with supplier, status, amounts, and delivery dates.', icon: ShoppingCart, category: 'Purchase Orders', color: 'bg-orange-500' },
  { id: 'pending-po', title: 'Pending Purchase Orders', description: 'POs awaiting approval or delivery.', icon: ShoppingCart, category: 'Purchase Orders', color: 'bg-amber-500' },
  { id: 'grn-report', title: 'Goods Received Report', description: 'All GRNs with accepted, rejected, and partial quantities.', icon: Package, category: 'Goods Receipt', color: 'bg-teal-500' },
  { id: 'rejected-goods', title: 'Rejected Goods Report', description: 'Items rejected at goods receipt with reasons.', icon: Package, category: 'Goods Receipt', color: 'bg-red-500' },
  { id: 'supplier-perf', title: 'Supplier Performance Report', description: 'Delivery timeliness, quality scores, and spend per supplier.', icon: Truck, category: 'Suppliers', color: 'bg-purple-500' },
  { id: 'spend-report', title: 'Procurement Spend Report', description: 'Total spend by period, farm, department, and category.', icon: DollarSign, category: 'Finance', color: 'bg-green-600' },
  { id: 'spend-category', title: 'Spend by Category', description: 'Breakdown of procurement spend by item category.', icon: BarChart3, category: 'Finance', color: 'bg-emerald-500' },
  { id: 'spend-supplier', title: 'Spend by Supplier', description: 'Total spend per supplier with trend analysis.', icon: Truck, category: 'Finance', color: 'bg-cyan-500' },
  { id: 'invoice-report', title: 'Supplier Invoice Report', description: 'All invoices with match status, payment status, and variances.', icon: Receipt, category: 'Finance', color: 'bg-violet-500' },
  { id: 'return-report', title: 'Procurement Return Report', description: 'Returns to suppliers with reasons and credit note status.', icon: RotateCcw, category: 'Returns', color: 'bg-rose-500' },
  { id: 'cycle-time', title: 'Procurement Cycle Time', description: 'Average time from requisition to goods receipt per category.', icon: TrendingUp, category: 'Analytics', color: 'bg-sky-500' },
  { id: 'budget-report', title: 'Budget vs Procurement Report', description: 'Budget allocation vs actual spend by department and category.', icon: DollarSign, category: 'Finance', color: 'bg-lime-600' },
];

const CATEGORIES = ['All', 'Requisitions', 'Purchase Orders', 'Goods Receipt', 'Suppliers', 'Finance', 'Returns', 'Analytics'];

// ─── Sample Report Preview ────────────────────────────────────────────────────
const SAMPLE_PR_DATA = [
  { number: 'PR-2026-0045', department: 'Feed Management', requestedBy: 'Okello James', priority: 'High', total: 25000000, status: 'Pending Approval', date: '2026-09-01' },
  { number: 'PR-2026-0044', department: 'Health & Vet', requestedBy: 'Dr. Apio Grace', priority: 'Urgent', total: 3600000, status: 'Approved', date: '2026-08-28' },
  { number: 'PR-2026-0043', department: 'Farm Operations', requestedBy: 'Waiswa Peter', priority: 'Normal', total: 1850000, status: 'Converted to PO', date: '2026-08-25' },
  { number: 'PR-2026-0042', department: 'Administration', requestedBy: 'Namukasa Rita', priority: 'Low', total: 450000, status: 'Completed', date: '2026-08-20' },
];

export default function ProcurementReportsContent() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [activeReport, setActiveReport] = useState<ReportDef | null>(null);
  const [filters, setFilters] = useState({ farm: 'all', dateFrom: '', dateTo: '', status: 'all' });

  const filtered = REPORTS.filter(r => {
    const matchCat = activeCategory === 'All' || r.category === activeCategory;
    const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const grouped = CATEGORIES.slice(1).reduce((acc, cat) => {
    const items = filtered.filter(r => r.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {} as Record<string, ReportDef[]>);

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Procurement Reports</h1>
          <p className="text-xs text-gray-400 mt-0.5">Generate, filter, and export procurement reports</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Left: Report List */}
        <div className="lg:col-span-1">
          {/* Search */}
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search reports..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${activeCategory === cat ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {cat}
              </button>
            ))}
          </div>

          {/* Report Cards */}
          <div className="space-y-4">
            {activeCategory === 'All' ? (
              Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">{cat}</div>
                  <div className="space-y-1.5">
                    {items.map(report => {
                      const Icon = report.icon;
                      return (
                        <button key={report.id} onClick={() => setActiveReport(report)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${activeReport?.id === report.id ? 'border-orange-300 bg-orange-50' : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'}`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${report.color}`}>
                            <Icon size={14} className="text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-gray-800 truncate">{report.title}</div>
                            <div className="text-xs text-gray-400 truncate">{report.description}</div>
                          </div>
                          <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="space-y-1.5">
                {filtered.map(report => {
                  const Icon = report.icon;
                  return (
                    <button key={report.id} onClick={() => setActiveReport(report)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${activeReport?.id === report.id ? 'border-orange-300 bg-orange-50' : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${report.color}`}>
                        <Icon size={14} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-gray-800 truncate">{report.title}</div>
                        <div className="text-xs text-gray-400 truncate">{report.description}</div>
                      </div>
                      <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: Report Preview */}
        <div className="lg:col-span-2">
          {!activeReport ? (
            <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col items-center justify-center py-20 text-center">
              <FileText size={48} className="text-gray-200 mb-4" />
              <div className="text-sm font-semibold text-gray-500">Select a report to preview</div>
              <div className="text-xs text-gray-400 mt-1">Choose from {REPORTS.length} available reports</div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              {/* Report Header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${activeReport.color}`}>
                    <activeReport.icon size={16} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{activeReport.title}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{activeReport.description}</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
                    <Printer size={12} /> Print
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-2 bg-orange-600 text-white text-xs font-semibold rounded-xl hover:bg-orange-700 transition-colors">
                    <Download size={12} /> Export
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/30 flex flex-wrap gap-3">
                <select value={filters.farm} onChange={e => setFilters(f => ({ ...f, farm: e.target.value }))} className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700">
                  <option value="all">All Farms</option>
                  <option value="idudi">Idudi Poultry Farm</option>
                  <option value="jinja">Jinja Branch</option>
                </select>
                <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700" placeholder="From date" />
                <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700" placeholder="To date" />
                <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="px-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700">
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="completed">Completed</option>
                </select>
                <button className="px-3 py-2 bg-orange-600 text-white text-xs font-semibold rounded-xl hover:bg-orange-700 transition-colors">
                  Generate Report
                </button>
              </div>

              {/* Report Data Preview */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wide">Report Preview — Idudi Poultry Farm</div>
                  <div className="text-xs text-gray-400">Generated: {new Date().toLocaleDateString('en-UG')}</div>
                </div>

                {/* Summary Row */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {[
                    { label: 'Total Records', value: '4' },
                    { label: 'Total Value', value: 'UGX 30.9M' },
                    { label: 'Approved', value: '2' },
                    { label: 'Pending', value: '1' },
                  ].map(s => (
                    <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center">
                      <div className="text-base font-bold text-gray-900">{s.value}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Number</th>
                        <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Department</th>
                        <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Requested By</th>
                        <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Priority</th>
                        <th className="text-right px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Total</th>
                        <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Status</th>
                        <th className="text-left px-3 py-2 font-bold text-gray-500 uppercase tracking-wide">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {SAMPLE_PR_DATA.map(row => (
                        <tr key={row.number} className="hover:bg-gray-50/50">
                          <td className="px-3 py-2.5 font-bold text-gray-900">{row.number}</td>
                          <td className="px-3 py-2.5 text-gray-700">{row.department}</td>
                          <td className="px-3 py-2.5 text-gray-600">{row.requestedBy}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              row.priority === 'Urgent' ? 'bg-orange-50 text-orange-700' :
                              row.priority === 'High' ? 'bg-amber-50 text-amber-700' :
                              row.priority === 'Low'? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-600'
                            }`}>{row.priority}</span>
                          </td>
                          <td className="px-3 py-2.5 text-right font-bold text-gray-900">{formatUGX(row.total)}</td>
                          <td className="px-3 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              row.status === 'Approved' ? 'bg-green-50 text-green-700' :
                              row.status === 'Pending Approval' ? 'bg-amber-50 text-amber-700' :
                              row.status === 'Completed'? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-indigo-700'
                            }`}>{row.status}</span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-400">{row.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex gap-2">
                  <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
                    <Download size={12} /> Export PDF
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
                    <Download size={12} /> Export Excel
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
                    <Download size={12} /> Export CSV
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
